import { Icon } from '@iconify/react';
import SearchableDropdown from '../../../components/SearchableDropdown';
import EmployeeShiftSection from './EmployeeShiftSection';
import {
    MARITAL_STATUS_OPTIONS,
    BLOOD_GROUP_OPTIONS,
    BANK_ACCOUNT_TYPE_OPTIONS,
    CONFIRM_ACCOUNT_KEY,
} from '../../../consts/employeeProfileFields';
import { DEPARTMENT_OPTIONS, EMPLOYMENT_TYPE_OPTIONS } from '../../SocietyController/ManageEmployee/Component/employeeConstants';

const labelClass = 'tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1';
const inputClass = 'tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500';
const errorClass = 'tw-text-[10px] tw-text-rose-600 tw-mt-1';

const Field = ({
    label, name, type = 'text', value, onChange, error, required,
    placeholder, as = 'input', options, rows, loading, disabled,
}) => (
    <div>
        <label className={labelClass}>
            {label}{required && <span className="tw-text-rose-500"> *</span>}
        </label>
        {as === 'textarea' ? (
            <textarea
                name={name}
                rows={rows || 3}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                disabled={disabled}
                className={inputClass}
            />
        ) : as === 'select' ? (
            <SearchableDropdown
                small
                value={value}
                // Clearing emits 'all' — store it as '' like an unselected field.
                onChange={(v) => onChange({ target: { name, value: v === 'all' ? '' : v } })}
                options={options || []}
                placeholder={`Select ${label}`}
                clearable={!required}
                disabled={disabled}
                loading={loading}
            />
        ) : (
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                disabled={disabled}
                autoComplete={type === 'password' ? 'new-password' : undefined}
                className={inputClass}
            />
        )}
        {error && <p className={errorClass}>{error}</p>}
    </div>
);

const EmployeeForm = ({
    formData, errors, onChange, onSubmit, onCancel, submitting, mode = 'add',
    employeeTypeOptions = [], employeeTypesLoading = false,
    // Access (RBAC) role — what the employee may do in the portal. Without an
    // explicit choice the backend derives one from the employee type, which is
    // how an Accountant ended up as a Society Administrator.
    roleOptions = [], rolesLoading = false,
    // null on add — there is no stored assignment to compare against yet.
    shiftBaseline = null,
}) => {
    const isAdd = mode === 'add';

    return (
        <form onSubmit={onSubmit} className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
            {/* Section: Basic */}
            <div className="tw-p-5 tw-border-b tw-border-gray-100">
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
                    <Icon icon="hugeicons:user-03" className="tw-text-rose-600" />
                    <h2 className="tw-text-sm tw-font-bold tw-text-gray-800">Basic Details</h2>
                </div>
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
                    <Field
                        label="Employee Type" name="employee_type" as="select" required
                        value={formData.employee_type} onChange={onChange} error={errors.employee_type}
                        options={employeeTypeOptions} loading={employeeTypesLoading}
                    />
                    <Field
                        label="Access Role" name="role_id" as="select" required
                        value={formData.role_id} onChange={onChange} error={errors.role_id}
                        options={roleOptions} loading={rolesLoading}
                    />
                    <Field
                        label="Full Name" name="name" required
                        value={formData.name} onChange={onChange} error={errors.name}
                        placeholder="e.g. Aarav Sharma"
                    />
                    <Field
                        label="User Name" name="username" required
                        value={formData.username} onChange={onChange} error={errors.username}
                        placeholder="e.g. aarav.sharma"
                    />
                    <Field
                        label="Phone Number" name="phone" required
                        value={formData.phone} onChange={onChange} error={errors.phone}
                        placeholder="10-digit number"
                    />
                    <Field
                        label="Email Address" name="email" type="email" required
                        value={formData.email} onChange={onChange} error={errors.email}
                        placeholder="name@example.com"
                    />
                    <Field
                        label="Joining Date" name="joining_date" type="date" required
                        value={formData.joining_date || ''} onChange={onChange} error={errors.joining_date}
                    />
                    <Field
                        label="Designation" name="designation"
                        value={formData.designation} onChange={onChange} error={errors.designation}
                        placeholder="e.g. Senior Accountant"
                    />
                    <Field
                        label="Department" name="department" as="select"
                        value={formData.department} onChange={onChange} error={errors.department}
                        options={DEPARTMENT_OPTIONS}
                    />
                    <Field
                        label="Employment Type" name="employment_type" as="select"
                        value={formData.employment_type} onChange={onChange} error={errors.employment_type}
                        options={EMPLOYMENT_TYPE_OPTIONS}
                    />
                    <div className="sm:tw-col-span-2 lg:tw-col-span-3">
                        <Field
                            label="Address" name="address" as="textarea" required
                            value={formData.address} onChange={onChange} error={errors.address}
                            placeholder="Street, City, State, PIN"
                        />
                    </div>
                </div>
            </div>

            {/* Section: Shift — hidden without shift.assign */}
            <EmployeeShiftSection
                values={formData}
                original={shiftBaseline}
                errors={errors}
                onChange={onChange}
                disabled={submitting}
            />

            {/* Section: Personal information */}
            <div className="tw-p-5 tw-border-b tw-border-gray-100">
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
                    <Icon icon="hugeicons:user-account" className="tw-text-rose-600" />
                    <h2 className="tw-text-sm tw-font-bold tw-text-gray-800">Personal Information</h2>
                </div>
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
                    <Field
                        label="Date of Birth" name="date_of_birth" type="date"
                        value={formData.date_of_birth} onChange={onChange} error={errors.date_of_birth}
                    />
                    <Field
                        label="Marital Status" name="marital_status" as="select"
                        value={formData.marital_status} onChange={onChange} error={errors.marital_status}
                        options={MARITAL_STATUS_OPTIONS}
                    />
                    <Field
                        label="Blood Group" name="blood_group" as="select"
                        value={formData.blood_group} onChange={onChange} error={errors.blood_group}
                        options={BLOOD_GROUP_OPTIONS}
                    />
                    <Field
                        label="Nationality" name="nationality"
                        value={formData.nationality} onChange={onChange} error={errors.nationality}
                        placeholder="e.g. Indian"
                    />
                    <Field
                        label="Qualification" name="qualification"
                        value={formData.qualification} onChange={onChange} error={errors.qualification}
                        placeholder="e.g. B.Com"
                    />
                    <Field
                        label="PAN Number" name="pan_number"
                        value={formData.pan_number} onChange={onChange} error={errors.pan_number}
                        placeholder="ABCDE1234F"
                    />
                    <Field
                        label="Aadhaar Number" name="aadhar_number"
                        value={formData.aadhar_number} onChange={onChange} error={errors.aadhar_number}
                        placeholder="12-digit number"
                    />
                </div>
            </div>

            {/* Section: Bank details */}
            <div className="tw-p-5 tw-border-b tw-border-gray-100">
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
                    <Icon icon="hugeicons:bank" className="tw-text-rose-600" />
                    <h2 className="tw-text-sm tw-font-bold tw-text-gray-800">Bank Details</h2>
                </div>
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
                    <Field
                        label="Account Holder Name" name="bank_account_holder_name"
                        value={formData.bank_account_holder_name} onChange={onChange} error={errors.bank_account_holder_name}
                    />
                    <Field
                        label="Bank Name" name="bank_name"
                        value={formData.bank_name} onChange={onChange} error={errors.bank_name}
                        placeholder="e.g. HDFC Bank"
                    />
                    <Field
                        label="Account Type" name="bank_account_type" as="select"
                        value={formData.bank_account_type} onChange={onChange} error={errors.bank_account_type}
                        options={BANK_ACCOUNT_TYPE_OPTIONS}
                    />
                    <Field
                        label="Account Number" name="bank_account_number"
                        value={formData.bank_account_number} onChange={onChange} error={errors.bank_account_number}
                        placeholder="6–30 digits"
                    />
                    <Field
                        label="Confirm Account Number" name={CONFIRM_ACCOUNT_KEY}
                        value={formData[CONFIRM_ACCOUNT_KEY]} onChange={onChange} error={errors[CONFIRM_ACCOUNT_KEY]}
                        placeholder="Re-enter account number"
                    />
                    <Field
                        label="IFSC Code" name="bank_ifsc_code"
                        value={formData.bank_ifsc_code} onChange={onChange} error={errors.bank_ifsc_code}
                        placeholder="HDFC0001234"
                    />
                    <Field
                        label="Branch Name" name="bank_branch_name"
                        value={formData.bank_branch_name} onChange={onChange} error={errors.bank_branch_name}
                    />
                    <Field
                        label="UPI ID" name="upi_id"
                        value={formData.upi_id} onChange={onChange} error={errors.upi_id}
                        placeholder="name@bank"
                    />
                </div>
            </div>

            {/* Section: Password (only on add) */}
            {isAdd && (
                <div className="tw-p-5 tw-border-b tw-border-gray-100">
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
                        <Icon icon="hugeicons:lock-password" className="tw-text-rose-600" />
                        <h2 className="tw-text-sm tw-font-bold tw-text-gray-800">Create Password</h2>
                    </div>
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
                        <Field
                            label="Password" name="password" type="password" required
                            value={formData.password} onChange={onChange} error={errors.password}
                            placeholder="At least 8 characters"
                        />
                        <Field
                            label="Confirm Password" name="confirm_password" type="password" required
                            value={formData.confirm_password} onChange={onChange} error={errors.confirm_password}
                            placeholder="Re-enter password"
                        />
                    </div>
                </div>
            )}

            {/* Footer */}
            <div className="tw-p-4 tw-flex tw-items-center tw-justify-end tw-gap-2 tw-bg-gray-50/50">
                <button
                    type="button"
                    onClick={onCancel}
                    className="tw-px-4 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-white"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={submitting}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-rounded-md disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                >
                    {submitting && <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent"></div>}
                    {isAdd ? 'Create Employee' : 'Save Changes'}
                </button>
            </div>
        </form>
    );
};

export default EmployeeForm;
